import React from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Stack,
    Divider,
    alpha,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
    Reddit as RedditIcon,
    Webhook as WebhookIcon,
    Star as StarIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';

const Guide = () => {
    const theme = useTheme();

    const sections = [
        {
            title: "Instagram Graph API Setup",
            icon: <InstagramIcon sx={{ color: '#E1306C' }} />,
            description: "Requires a long-lived access token from a Facebook Developer App connected to your Instagram account.",
            steps: [
                "Convert Instagram to a Business/Creator Account",
                "Link to a Facebook Page",
                "Get a Long-Lived Access Token (60 days) via Token Debugger",
                "Enable 'instagram_manage_comments' and 'instagram_manage_messages'"
            ]
        },
        {
            title: "YouTube Automation (OAuth 2.0)",
            icon: <YouTubeIcon sx={{ color: '#FF0000' }} />,
            description: "YouTube uses OAuth 2.0 for secure posting. Follow these specific steps in the Google Cloud Console to get your credentials.",
            steps: [
                "1. Create a Project: Go to Google Cloud Console and select 'New Project'.",
                "2. Enable API: Search for 'YouTube Data API v3' in the Library and click 'Enable'.",
                "3. OAuth Consent Screen: Set to 'External' and provide your email in the 'OAuth consent screen' tab.",
                "4. Create Credentials: Create an 'OAuth client ID' as a 'Desktop app'.",
                "5. Download Secret: Download the JSON file to find your Client ID and Client Secret strings."
            ]
        },
        {
            title: "Facebook Page Management",
            icon: <FacebookIcon sx={{ color: '#1877F2' }} />,
            description: "Requires a Page Access Token and your unique Facebook Page ID.",
            steps: [
                "Go to Facebook Developer Console",
                "Generate a Page Access Token for your specific Page",
                "Find your Page ID in the Page 'About' section",
                "Ensure 'pages_manage_posts' permission is active"
            ]
        },
        {
            title: "Twitter (X) - OAuth 1.0a",
            icon: <TwitterIcon sx={{ color: '#000000' }} />,
            description: "Requires a Twitter Developer App with OAuth 1.0a User Context permissions. All five credentials are required.",
            steps: [
                "Apply for Elevated/Pro access on Twitter Developer Portal",
                "Enable 'Read and Write' permissions",
                "Generate API Key, API Secret, Access Token, and Access Secret",
                "Ensure 'User Context' is enabled in App Settings"
            ]
        },
        {
            title: "LinkedIn Professional API",
            icon: <LinkedInIcon sx={{ color: '#0A66C2' }} />,
            description: "Requires an OAuth 2.0 access token with specific scopes for profile and social sharing.",
            steps: [
                "Create a LinkedIn Developer App",
                "Verify your Page/Company association",
                "Add 'Share on LinkedIn' and 'Sign In with LinkedIn' products",
                "Request 'r_liteprofile' and 'w_member_social' scopes"
            ]
        },
        {
            title: "Reddit Script Automation",
            icon: <RedditIcon sx={{ color: '#FF4500' }} />,
            description: "Create a 'script' type app at reddit.com/prefs/apps for automated interaction.",
            steps: [
                "Navigate to Reddit App Preferences",
                "Click 'create another app...' and select 'script'",
                "Set redirect URI to http://localhost:8080 (placeholder)",
                "Use your Reddit username, password, client_id, and secret"
            ]
        },
        {
            title: "AI Engagement & Webhooks",
            icon: <WebhookIcon color="info" />,
            setup: {
                url: "BACKEND_PUBLIC_URL/webhook/instagram",
                tokens: ["comments", "messages"]
            },
            description: "To get instant lead detection, configure your Meta Webhook callback URL to point to your backend."
        }
    ];

    return (
        <Box>
            <Box sx={{ mb: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                    Platform Setup Guide
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Follow these steps to enable AI Lead Generation and Auto-Replies.
                </Typography>
            </Box>

            <Grid container spacing={4}>
                <Grid xs={12} lg={8}>
                    <Stack spacing={3}>
                        {sections.map((section, idx) => (
                            <Accordion
                                key={idx}
                                defaultExpanded={idx === 0}
                                sx={{
                                    borderRadius: 4,
                                    '&:before': { display: 'none' },
                                    bgcolor: alpha(theme.palette.background.paper, 0.4),
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    overflow: 'hidden'
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {section.icon}
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{section.title}</Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 4, pb: 4 }}>
                                    {section.steps && (
                                        <List>
                                            {section.steps.map((step, sIdx) => (
                                                <ListItem key={sIdx} disableGutters>
                                                    <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                                                    <ListItemText primary={step} primaryTypographyProps={{ fontWeight: 600 }} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                    {section.permissions && (
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            {section.permissions.map((p, pIdx) => (
                                                <Grid xs={12} sm={6} key={pIdx}>
                                                    <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                                                        <CardContent sx={{ p: '16px !important' }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 900, color: 'secondary.main', display: 'block', mb: 0.5 }}>
                                                                {p.label}
                                                            </Typography>
                                                            <Typography variant="body2">{p.desc}</Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                    {section.setup && (
                                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 3, border: `1px dashed ${theme.palette.info.main}` }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Callback URL:</Typography>
                                            <Typography variant="code" sx={{
                                                display: 'block',
                                                p: 1.5,
                                                bgcolor: 'background.paper',
                                                borderRadius: 2,
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem',
                                                mb: 2
                                            }}>
                                                {section.setup.url}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Subscribe to: {section.setup.tokens.join(', ')}</Typography>
                                        </Box>
                                    )}
                                    {section.details && (
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{section.details}</Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Grid>

                <Grid xs={12} lg={4}>
                    <Card sx={{
                        borderRadius: 6,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
                        color: 'white',
                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.3)}`
                    }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <StarIcon />
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>Pro Tips</Typography>
                            </Box>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Lead Detection</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>AI looks for pricing, availability, and specific buying questions automatically.</Typography>
                                </Box>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Safety First</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Our multi-stage filters block harassment and hate speech before the AI even draft a reply.</Typography>
                                </Box>
                                <Box sx={{ pt: 2 }}>
                                    <Chip label="Real-time Ready" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 800 }} />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Guide;
